import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { hardware } from "../data/site";

/**
 * BHR Paints & Hardware — sister business section.
 * Full-bleed with a store hero + address panel + brand strip.
 */
export default function Hardware() {
    return (
        <section
            id="hardware"
            className="relative bg-[#0b0b0b] py-24 md:py-40 px-6 md:px-10 border-y border-[rgba(212,175,55,0.2)] overflow-hidden"
            data-testid="hardware-section"
        >
            <div className="mx-auto max-w-[1440px]">
                <div className="flex items-center gap-6 mb-10">
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 03 — The Store
                    </span>
                </div>

                <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 lg:col-span-8 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                    >
                        BHR Paints
                        <br />
                        &amp; <em className="text-[#E2C56D]">Hardware.</em>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="col-span-12 lg:col-span-4 text-[#bebebe] text-base leading-relaxed font-light"
                    >
                        {hardware.tagline} A trusted authorised dealer in Shanthinagar for
                        every branded material a project needs.
                    </motion.p>
                </div>

                {/* Split: description card + store image */}
                <div className="grid grid-cols-12 gap-6 md:gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 md:col-span-5 border border-[rgba(212,175,55,0.25)] p-8 md:p-10 bg-[#111111]/70 backdrop-blur-xl flex flex-col justify-between"
                    >
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C] mb-4">
                                Authorised Dealer
                            </div>
                            <p className="text-white text-lg md:text-xl font-light font-serif-alt leading-relaxed">
                                {hardware.description}
                            </p>
                        </div>

                        <div className="mt-10 pt-8 border-t border-[rgba(212,175,55,0.2)] space-y-4 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a] mb-2">
                                    Location
                                </div>
                                <div className="text-white/95 font-light">{hardware.address}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a] mb-2">
                                    Phone
                                </div>
                                <div className="flex flex-col gap-1">
                                    {hardware.phones.map((p, i) => (
                                        <a
                                            key={p}
                                            href={`tel:${hardware.phonesRaw[i]}`}
                                            className="text-white hover:text-[#E2C56D] font-light"
                                            data-testid={`hardware-phone-${i}`}
                                        >
                                            {p}
                                        </a>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-4 flex flex-wrap gap-3">
                                <a
                                    href={hardware.mapLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-sheen inline-flex items-center gap-3 px-6 py-3 bg-[#C89B3C] text-[#0b0b0b] text-[10px] uppercase tracking-[0.32em] font-medium hover:bg-[#E2C56D] transition-colors"
                                    data-testid="hardware-map-btn"
                                >
                                    <span className="relative z-[2]">Get Directions</span>
                                    <span className="relative z-[2]">→</span>
                                </a>
                                <a
                                    href={`https://wa.me/${hardware.phonesRaw[0]}?text=${encodeURIComponent("Hello BHR Paints & Hardware — I have an enquiry.")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-3 px-6 py-3 border border-[rgba(212,175,55,0.55)] text-white text-[10px] uppercase tracking-[0.32em] hover:bg-white/5 hover:border-[#E2C56D] transition-colors"
                                    data-testid="hardware-whatsapp-btn"
                                >
                                    WhatsApp Enquiry
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    <motion.figure
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="col-span-12 md:col-span-7 relative overflow-hidden aspect-[16/11] group"
                    >
                        <img
                            src={hardware.image}
                            alt="BHR Paints & Hardware store"
                            className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-[1.04]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0b]/50 via-transparent to-[#0b0b0b]/80" />
                        <div className="absolute inset-0 border border-[rgba(212,175,55,0.35)]" />
                        <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] uppercase tracking-[0.35em] text-white/95">
                            <span className="flex items-center gap-3">
                                <span className="w-2 h-2 rounded-full bg-[#E2C56D] animate-pulse" />
                                OPEN · Mon — Sat
                            </span>
                            <span>Shanthinagar</span>
                        </div>
                        <div className="absolute left-6 right-6 bottom-6 md:left-8 md:right-8 md:bottom-8 flex items-end justify-between gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C] mb-2">
                                    Est. Since Generations
                                </div>
                                <div className="font-display text-white text-3xl md:text-4xl leading-tight">
                                    {hardware.name}
                                </div>
                            </div>
                        </div>
                    </motion.figure>
                </div>

                {/* Brand strip */}
                <div className="mt-16 md:mt-20">
                    <div className="flex items-center gap-4 mb-8">
                        <span className="w-10 h-px bg-[#C89B3C]" />
                        <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                            Trusted Brands · Authorised Dealer
                        </span>
                        <span className="flex-1 h-px bg-[rgba(212,175,55,0.18)]" />
                    </div>

                    {/* Grid of brand text-marks (monochrome, gold on hover) */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[rgba(212,175,55,0.18)]">
                        {hardware.brandNames.map((b, i) => (
                            <motion.div
                                key={b}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="group bg-[#0b0b0b] flex items-center justify-center h-24 md:h-28 relative overflow-hidden"
                                data-testid={`hardware-brand-${i}`}
                            >
                                <span className="font-display italic text-2xl md:text-3xl text-white/80 group-hover:text-[#E2C56D] transition-colors tracking-tight text-center px-4">
                                    {b}
                                </span>
                                <span className="absolute bottom-3 right-3 text-[9px] uppercase tracking-[0.3em] text-[#7a7a7a]">
                                    №{String(i + 1).padStart(2, "0")}
                                </span>
                            </motion.div>
                        ))}
                    </div>

                    {/* Marquee reinforcement */}
                    <div className="mt-6 marquee-fade">
                        <Marquee gradient={false} speed={28} pauseOnHover={false}>
                            {[...hardware.brandNames, ...hardware.brandNames].map((b, i) => (
                                <span
                                    key={`${b}-${i}`}
                                    className="font-display italic text-lg md:text-xl mx-8 text-outline-gold tracking-tight"
                                >
                                    {b} <span className="mx-4 text-[#C89B3C]/60">◆</span>
                                </span>
                            ))}
                        </Marquee>
                    </div>
                </div>
            </div>
        </section>
    );
}
